import { TFunction } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";

import {
    EventType,
    localizeEvent,
} from "utils/controlResults/activityStatusUtils";

type GroupType = "PendingOnAnyReviewer" | "OverdueOnAnyReviewer";
type GroupOrEventType = GroupType | EventType;

/**
 * A single node in the status filter cascader. `value` is always an `EventType`
 * token so the stored filter value is locale-independent.
 */
export type StatusCascaderOption =
    | {
          value: EventType;
          label: string;
      }
    | {
          value: GroupType;
          label: string;
          children: Extract<StatusCascaderOption, { value: EventType }>[];
      };

/**
 * Reviewer statuses are grouped: the generic (aggregate) event type is the
 * parent, the 1st/2nd variants are its children.
 */
interface ReviewerGroup {
    aggregate: GroupType;
    children: EventType[];
}

const REVIEWER_GROUPS: ReviewerGroup[] = [
    {
        aggregate: "PendingOnAnyReviewer",
        children: ["PendingOnFirstReviewer", "PendingOnSecondReviewer"],
    },
    {
        aggregate: "OverdueOnAnyReviewer",
        children: ["OverdueOnFirstReviewer", "OverdueOnSecondReviewer"],
    },
];

/**
 * Canonical option order. Later lifecycle steps sort last (responsible →
 * reviewer → 2nd reviewer → completed); within a step the overdue variant is
 * heavier, so it sorts right after its pending counterpart.
 */
const STATUS_SORT_ORDER: GroupOrEventType[] = [
    "PendingOnResponsible",
    "OverdueOnResponsible",
    "PendingOnAnyReviewer",
    "PendingOnFirstReviewer",
    "OverdueOnAnyReviewer",
    "OverdueOnFirstReviewer",
    "PendingOnSecondReviewer",
    "OverdueOnSecondReviewer",
    "Completed",
    "CompletedAfterDueDate",
];

const byStatusOrder = (
    a: { value: GroupOrEventType },
    b: { value: GroupOrEventType }
): number =>
    STATUS_SORT_ORDER.indexOf(a.value) - STATUS_SORT_ORDER.indexOf(b.value);

/**
 * Static option tree. It always includes both the aggregate and detailed
 * reviewer options, regardless of which statuses are present in the data.
 */
export function buildStatusCascaderOptions(
    t: TFunction
): StatusCascaderOption[] {
    const leaf = (value: EventType) => ({
        value,
        label: localizeEvent(value, t),
    });

    const group = ({
        aggregate,
        children,
    }: ReviewerGroup): StatusCascaderOption => ({
        value: aggregate,
        label: localizeGroup(aggregate, t),
        children: children.map(leaf).sort(byStatusOrder),
    });

    return [
        leaf("PendingOnResponsible"),
        leaf("OverdueOnResponsible"),
        group(REVIEWER_GROUPS[0]),
        group(REVIEWER_GROUPS[1]),
        leaf("Completed"),
        leaf("CompletedAfterDueDate"),
    ].sort(byStatusOrder);
}

/**
 * Expand stored tokens into the set of event types they match. An aggregate
 * reviewer token matches the single-reviewer status plus both detailed
 * variants, so selecting it returns the union (preserving pre-split behavior).
 */
export function expandStatusFilterTokens(tokens: string[]): GroupOrEventType[] {
    return tokens.flatMap((token) => {
        const group = REVIEWER_GROUPS.find((g) => g.aggregate === token);
        if (group) {
            return [group.aggregate, ...group.children];
        }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        return [token as EventType];
    });
}

/** True when the row's status matches any of the selected tokens. */
export function matchesStatusFilter(
    status: GroupOrEventType,
    tokens: string[]
): boolean {
    return expandStatusFilterTokens(tokens).includes(status);
}

/**
 * Map stored tokens to cascader paths. An aggregate token expands to both child
 * paths so antd renders the parent as checked; a detailed token maps to its
 * single child path; everything else is a top-level leaf.
 */
export function statusTokensToCascaderValue(tokens: string[]): string[][] {
    return tokens.flatMap((token) => {
        const aggregateGroup = REVIEWER_GROUPS.find(
            (g) => g.aggregate === token
        );
        if (aggregateGroup) {
            return aggregateGroup.children.map((child) => [
                aggregateGroup.aggregate,
                child,
            ]);
        }
        const parentGroup = REVIEWER_GROUPS.find((g) =>
            g.children.includes(token as EventType)
        );
        if (parentGroup) {
            return [[parentGroup.aggregate, token]];
        }
        return [[token]];
    });
}

/**
 * Map cascader paths back to stored tokens. A reviewer parent whose children
 * are all selected collapses to the aggregate token; otherwise the individual
 * child tokens are kept.
 */
export function cascaderValueToStatusTokens(paths: string[][]): string[] {
    const leafTokens = paths
        .filter((path) => path.length === 1)
        .map((path) => path[0]);

    const childPaths = paths.filter((path) => path.length === 2);
    const reviewerTokens = REVIEWER_GROUPS.flatMap((group) => {
        const selectedChildren = childPaths
            .filter((path) => path[0] === group.aggregate)
            .map((path) => path[1]);
        if (selectedChildren.length === 0) {
            return [];
        }
        const allSelected = group.children.every((child) =>
            selectedChildren.includes(child)
        );
        return allSelected ? [group.aggregate] : selectedChildren;
    });

    return [...leafTokens, ...reviewerTokens];
}

export function localizeGroup(groupType: GroupType, t: TFunction): string {
    switch (groupType) {
        case "PendingOnAnyReviewer":
            return t("DashboardControlActivities_Event_PendingOnAnyReviewer");
        case "OverdueOnAnyReviewer":
            return t("DashboardControlActivities_Event_OverdueOnAnyReviewer");
        default:
            return assertNever(groupType);
    }
}
